/**
 * **Le mode découverte** — avenant **A-9**, exigence de méthode n° 2 — **dégonflé par ⟦A-129 ·
 * C11⟧**.
 *
 * Dans les mots de Mathieu, playtest A-9 : *« le jeu s'arrête, tu as tout qui est un peu noir,
 * un peu fond opaque, et tu as une pop-up qui t'explique les trucs avant que ça démarre »*. Puis,
 * playtest A-129, sur ce que ce module était devenu : *« tu te fais arrêter toutes les 3
 * secondes pour t'expliquer un truc […] qui n'a pas trop d'incidence »*.
 *
 * **Deux natures de leçon cohabitent désormais, et c'est le cœur du chantier C11 :**
 *
 * - **`bloquante: true`** — le voile opaque d'avant, plein écran, le jeu à l'arrêt. Réduit à
 *   4 leçons : le briefing d'ouverture (fusion `abonnes` + `publier`, tour 0), le premier
 *   événement, l'horloge/dormir, et les commentaires/santé mentale (celle qui annonce la fin de
 *   partie à zéro). Ce sont les seules dont rater l'explication coûte cher.
 * - **`bloquante: false`** — une **info-bulle non bloquante**, ancrée sur l'élément concerné
 *   (`ancre`), une seule à l'écran à la fois, fermée par n'importe quel clic — **le jeu continue
 *   derrière**. Toutes les autres leçons d'état : énergie, veille, fil, image, tendances, arbre,
 *   réputation, fidélité, momentum, longue, temps, argent.
 *
 * Les deux natures partagent la MÊME table (`LECONS`) et le même contrat de contenu
 * (`corps`/`aRetenir`/`quand`) : `prochaineLecon` ne regarde que les bloquantes,
 * `prochaineInfoBulle` que les autres. Une leçon ne change jamais de camp silencieusement — le
 * champ `bloquante` est écrit en clair sur chacune, jamais déduit.
 *
 * Les leçons de **mini-jeu** (`LECONS_MINIJEU`) ne passent plus par ce mécanisme du tout depuis
 * C11 : leur contenu est fusionné dans l'écran d'intro de l'Atelier lui-même (`Atelier.tsx`),
 * première fois seulement — un seul écran, jamais une modale séparée.
 *
 * ## Ce qui ne change pas
 *
 * 1. **L'ordre est celui de l'arc 1 d'A-9**, littéralement : le briefing (compteur d'abonnés +
 *    publier) · le premier événement · l'horloge et dormir · l'énergie · la veille · les
 *    commentaires et la santé mentale · l'image de chaîne · les tendances · l'arbre. Les leçons
 *    qui n'appartiennent pas à l'arc 1 (les trois jauges secondaires, la longue, l'argent)
 *    viennent après dans la table.
 * 2. **Une leçon BLOQUANTE par tour** (sauf le briefing d'ouverture, au tour 0). Les info-bulles
 *    n'ont pas cette contrainte : « une seule à la fois » leur suffit, le jeu ne s'arrêtant plus.
 * 3. **Aucune constante de moteur recopiée.** Les chiffres cités dans les textes sont importés
 *    de `@maxime/shared` : ils sont marqués ✎ et bougeront en M3.12 (recalibrage). Une leçon qui
 *    ment sur un seuil est pire que pas de leçon.
 *
 * Les seuils de zone de santé mentale (60 / 30), eux, sont de l'**affichage** : ils vivent dans
 * `game/format.ts` (`zoneSM`) et sont repris de là, pas du moteur.
 */

import {
  H_EVEILLE_PAR_JOUR,
  H_JOUR,
  H_NUIT,
  MOMENTUM_DECAY_PAR_JOUR,
  MULT_CRUNCH,
  MULT_FATIGUE,
  PALIER_GRAND_SAUT,
  PC_PAR_NIVEAU,
  PC_PAR_PALIER,
  RPM_LONGUE,
  RPM_SHORT,
  SM_NUIT,
  SM_NUIT_CONTENUE,
  SEUIL_CHAOS,
  SEUIL_CRUNCH,
  SEUIL_LUMIERE,
  retiresParLeMenage,
  type AtelierStage,
  type GameState,
} from "@maxime/shared";

import { contenu } from "./content";
import { etage, ouvert, type EtageId } from "./escalier";
import { points } from "./format";

export type LeconId = string;

export interface Lecon {
  id: LeconId;
  icone: string;
  titre: string;
  /** Le sous-titre : de quoi on parle, en trois mots. */
  chapeau: string;
  /** Les paragraphes, dans l'ordre. Deux ou trois — au-delà, personne ne lit. */
  corps: readonly string[];
  /** LA phrase à retenir, encadrée. Si le joueur ne lit qu'une ligne, c'est celle-ci. */
  aRetenir: string;
  /**
   * L'étage d'escalier dont cette leçon **prend en charge l'annonce** ⟦A-5⟧ : quand elle est
   * due, `MomentsDebloques` se tait — sinon le joueur reçoit deux pop-ups à la suite qui
   * disent la même chose.
   *
   * ⚠️ L'annonce n'est **pas recopiée ici** : `paragraphes()` va la chercher dans la table de
   * l'escalier au moment du rendu. Les seuils de l'escalier bougent (ils sont passés des
   * abonnés au compteur de publications avec A-9) et un texte de leçon qui les aurait
   * recopiés mentirait dès le premier réglage.
   */
  etage?: EtageId;
  /**
   * Le déclencheur, lu sur l'état de la run. Absent = leçon déclenchée **impérativement**
   * (les mini-jeux : c'est l'Atelier qui la demande, juste avant de monter le jeu).
   */
  quand?(state: GameState): boolean;
  /**
   * ⟦A-129 · C11⟧ **`true`** = voile opaque plein écran, le jeu à l'arrêt (au plus 4 dans toute
   * la table). **`false`** = info-bulle non bloquante, ancrée sur `ancre`, le jeu continue
   * derrière. Absent sur les leçons de `LECONS_MINIJEU` : elles ne passent plus par ce
   * mécanisme, `bloquante` n'a donc aucun sens pour elles.
   */
  bloquante?: boolean;
  /**
   * ⟦A-129 · C11⟧ L'élément DOM sur lequel ancrer l'info-bulle (`data-decouverte-ancre="…"` côté
   * écran). Ignoré si `bloquante`. Absent = l'info-bulle se pose en position par défaut — c'est
   * un repli valide, jamais une erreur : « une info-bulle dont l'ancre n'existe pas à l'écran ne
   * doit jamais bloquer ni planter ».
   */
  ancre?: string;
  /**
   * ⟦C21 · A-146 §2⟧ **Les répliques de Matt**, dans l'ordre — la chambre les joue une à une
   * dans `BoiteDialogue` (C20), avancées au ▶ (clic/Entrée/Espace), au lieu du mur de
   * paragraphes de `corps` (voile bloquant ou bulle ancrée, d'avant ce chantier — ces deux
   * surfaces ne se montent plus sur la chambre, `game/parole.ts`). Deux à quatre phrases
   * courtes pour les leçons bloquantes ; absent pour les info-bulles non bloquantes, qui n'en
   * ont besoin que d'UNE — `repliquesDe()` retombe alors sur `[aRetenir]`, déjà la phrase la
   * plus courte et la plus parlée du lot, jamais retapée ici en double.
   *
   * `corps`/`texte` ne bougent pas : ils restent la source pour `paragraphes()`, toujours lue
   * par `ModeDecouverte`/`InfoBulleDecouverte` — retirées de la chambre, pas supprimées, au cas
   * où un écran hors chambre en ait besoin un jour.
   */
  repliques?: readonly string[];
  /**
   * ⟦C57 · lot 4 · A-174⟧ **Les répliques COURTES de Matt** : une idée par réplique, UNE phrase de 12 mots
   * au plus (« une réplique de Matt = une idée, une ligne »). Écrites À CÔTÉ de `repliques`, jamais à sa
   * place : le texte long n'est pas réécrit, il reste la source (et l'accueil de Matt, `REPLIQUES_BRIEFING`,
   * continue de le dire avec sa voix). **Même nombre, même ordre que `repliques`** : les halos de
   * `game/parole.ts` sont posés par rang de réplique, un test le verrouille. `repliquesDe()` lit celles-ci
   * d'abord ; les leçons sans `repliques_courtes` (celles de `LECONS_MINIJEU`, lot 8) retombent sur `repliques`.
   */
  repliques_courtes?: readonly string[];
  /**
   * ⟦C57 · lot 3 · A-174⟧ Le titre quand `titre` dépasse 5 mots (la règle). Absent = `titre` tel quel.
   */
  titre_court?: string;
  /**
   * ⟦C57 · lot 3 · A-174⟧ **« À retenir » en UNE phrase de 12 mots au plus** — ce que le voile hors chambre
   * (`ModeDecouverte`) et l'info-bulle (`InfoBulleDecouverte`) gardent à l'écran. `aRetenir` (le texte
   * d'avant) n'est pas réécrit : il reste la source, et les `corps` s'ouvrent par « Les dessous ». Absent =
   * `aRetenir` tel quel (il tenait déjà dans la règle).
   */
  a_retenir_court?: string;
}

/** ⟦C57 · lot 3⟧ Le titre à l'écran : le court s'il existe. */
export function titreCourtDe(l: Lecon): string {
  return l.titre_court ?? l.titre;
}

/** ⟦C57 · lot 3⟧ « À retenir » à l'écran : la phrase courte s'il en existe une. */
export function aRetenirCourtDe(l: Lecon): string {
  return l.a_retenir_court ?? l.aRetenir;
}

/**
 * ⟦C21 · A-146 §2⟧ Les répliques effectives d'une leçon : les siennes si elle en a écrit, sinon
 * `[aRetenir]` — jamais un texte dupliqué à la main. Utilisée par `game/parole.ts`, qui ne
 * connaît que des `Lecon`, jamais leur contenu par cœur.
 *
 * ⟦C57 · lot 4⟧ Ce que Matt DIT à l'écran est la version courte : `repliques_courtes` d'abord, puis
 * `repliques` (les leçons de mini-jeu, pas encore raccourcies), puis le « À retenir » court.
 */
export function repliquesDe(l: Lecon): readonly string[] {
  return l.repliques_courtes ?? l.repliques ?? [aRetenirCourtDe(l)];
}

/**
 * ⟦C22 · A-151⟧ Les répliques de l'accueil, **version 1 (« l'aveu des 48 h »)**, validée par
 * Mathieu le 12/09 (`docs/REPLIQUES-MATT.md`) : *« peu de méta dans le jeu, mais Matt explique
 * qu'on est sur le 48 h »*. Ce sont aussi les répliques de la leçon `briefing` ci-dessous.
 * ⟦C30 · lot G⟧ `screens/Intro/Intro.tsx` (l'écran juste avant la chambre) ne rejoue plus aucune
 * réplique : `screens/Accueil/Accueil.tsx`, juste après, est désormais le SEUL endroit qui joue
 * la PREMIÈRE, avec sa voix et son attente de clic (⟦C29 · lot D⟧), jamais un texte dit deux fois.
 * La 4e réplique porte une correction de texte, validée séparément (13/09) : « zéro au million »
 * s'entendait mal à l'oral, devenu « on part de zéro […] jusqu'à un million ».
 */
export const REPLIQUES_BRIEFING = [
  "Salut, moi c'est Matt. Ce jeu, je l'ai monté en 48 h, excuse le décor un peu bricolé.",
  "Toi, tu pars pareil que moi à l'époque : zéro abonné, une webcam de PC portable, ta chambre.",
  "On est à Castelclic. Ici, personne n'a jamais dépassé les mille abonnés avant toi.",
  "On part de zéro, et on va jusqu'à un million, avec une rue à ton nom au bout. Vas-y, sois le premier du village !",
] as const;

const PCT_CRUNCH = Math.round((1 - MULT_CRUNCH) * 100);
const PCT_FATIGUE = Math.round((1 - MULT_FATIGUE) * 100);
const PCT_MOMENTUM_JOUR = Math.round(MOMENTUM_DECAY_PAR_JOUR * 100);

/**
 * Les leçons pilotées par l'état. **L'ordre de cette table est l'ordre des pop-ups** : quand
 * plusieurs conditions sont vraies en même temps, c'est la première non vue qui passe.
 */
export const LECONS: readonly Lecon[] = [
  // ───────────────────────── Arc 1 · les gestes ─────────────────────────
  // ⟦A-129 · C11⟧ Fusion de `abonnes` + `publier` — deux pop-ups à la file au tour 0 sont
  // devenues UNE seule carte. Rien n'est perdu : les deux idées tiennent à deux paragraphes.
  {
    id: "briefing",
    icone: "👥",
    titre: "Bienvenue sur MeTube",
    chapeau: "Ton score, et le verbe du jeu",
    corps: [
      "En haut à gauche, le gros chiffre : c'est toi. Zéro, pour l'instant — comme tout le monde, y compris ceux que tu regardes le soir. La barre en dessous ne vise pas le million : elle vise ton prochain palier, le premier à 100 abonnés.",
      "Une seule chose fait grandir une chaîne : publier. Le bouton « Publier un short » ouvre l'Atelier — une idée, trois mini-jeux, et ça part en ligne. Une vidéo ne se juge pas le soir même : les vues tombent nuit après nuit, pas d'un coup.",
    ],
    aRetenir: "Le million, c'est l'horizon. Publier, c'est le seul geste qui compte — recommence.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Publier, c'est le seul geste qui compte.",
    bloquante: true,
    quand: () => true,
    // ⟦C22 · A-151⟧ La leçon rejoue désormais l'accueil version 1 (`REPLIQUES_BRIEFING`
    // ci-dessus), les 4 répliques comprises : même texte que `screens/Accueil/Accueil.tsx`,
    // jamais retapé en double (⟦C30 · lot G⟧ : c'est `Accueil.tsx` qui les dit, avec la voix ;
    // `stores/accueilStore.ts` marque `briefing` vue à la fin de l'accueil, donc cette entrée ne
    // rejoue rien pour une création normale, seulement pour une run qui n'est jamais passée par
    // l'accueil, `screens/Intro/Intro.tsx` ne portant elle-même plus aucune réplique). Halo
    // (`game/parole.ts`, `HALO_BRIEFING`) : rien sur la 1re et la 3e, le panneau « Ma chaîne » sur
    // la 2e, le bouton Publier sur la 4e (à retenir), avant ⟦C22⟧ c'était l'inverse (1re et 2e),
    // verbatim de l'ancien texte du briefing, retiré avec lui.
    repliques: REPLIQUES_BRIEFING,
    // ⟦C57 · lot 4⟧ Mêmes quatre répliques, une idée chacune (le rang décide du halo : 2e = « Ma chaîne », 4e = Publier).
    // `REPLIQUES_BRIEFING` reste intact : l'accueil de Matt (écran à part, avec sa voix) le dit tel quel.
    repliques_courtes: [
      "Moi c'est Matt, j'ai monté ce jeu en 48 h.",
      "Toi, tu pars de zéro : zéro abonné, une webcam, ta chambre.",
      "Castelclic : personne n'y a jamais dépassé mille abonnés.",
      "Zéro au million, une rue à ton nom au bout : vas-y !",
    ],
  },
  {
    id: "evenement",
    icone: "📨",
    titre: "Il t'arrive quelque chose",
    chapeau: "Les événements",
    corps: [
      "La vie d'un créateur, ce n'est pas que des vidéos : un commentaire qui dérape, une opportunité qui passe, du matos qui lâche. Ça, c'est un événement — et il attend une décision de toi.",
      "Tant qu'il est là, plus rien d'autre n'est possible : ni publier, ni dormir. Il n'y a pas de bonne réponse universelle, il y a la tienne — et elle laisse des traces sur tes jauges.",
    ],
    aRetenir: "Un événement bloque tout le reste. Tranche, et le jeu repart.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Un événement bloque tout : tranche, et le jeu repart.",
    bloquante: true,
    quand: (s) => s.pending_event !== null,
    // ⟦C21 · A-146 §2.1⟧ Découpé, pas réécrit : même sens que `corps`, en phrases courtes.
    repliques: [
      "La vie d'un créateur, ce n'est pas que des vidéos : un commentaire qui dérape, une opportunité qui passe, du matos qui lâche. Ça, c'est un événement.",
      "Tant qu'il est là, plus rien d'autre n'est possible : ni publier, ni dormir.",
      "Il n'y a pas de bonne réponse universelle, il y a la tienne. Tranche, et le jeu repart.",
    ],
    // ⟦C57 · lot 4⟧ Trois répliques, une idée chacune.
    repliques_courtes: [
      "Un commentaire qui dérape, du matos qui lâche : c'est un événement.",
      "Tant qu'il est là, ni publier, ni dormir.",
      "Pas de bonne réponse, juste la tienne : tranche.",
    ],
  },
  {
    id: "horloge",
    icone: "🕰",
    titre: "L'horloge, et le lit",
    chapeau: "Le temps",
    corps: [
      `En haut à droite : « J1 · 3 h/${H_JOUR} ». Une journée simulée fait ${H_JOUR} heures — ${H_EVEILLE_PAR_JOUR} h de travail et ${H_NUIT} h de nuit —, et chaque action en croque un morceau. Un short en prend plusieurs : tu ne feras pas tout, tous les jours.`,
      `Dormir coûte ${H_NUIT} h et fait avancer l'horloge : c'est la nuit qui fait tourner le monde. Tes vidéos continuent de travailler, les vues tombent, les abonnés arrivent, tu récupères ton énergie — et ce que tu as laissé traîner (idées, tendances, opportunités) vieillit d'autant.`,
    ],
    aRetenir: `Agir consomme des heures, dormir aussi (${H_NUIT} h). La nuit encaisse les résultats.`,
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Agir et dormir coûtent des heures : la nuit encaisse les résultats.",
    bloquante: true,
    quand: (s) => s.counters.videos_published >= 1,
    // ⟦C21 · A-146 §2.1⟧ Découpé, pas réécrit. Halo (`game/parole.ts`, `HALO_HORLOGE`) : l'horloge
    // de l'en-tête sur la première réplique, le bouton Dormir sur la troisième.
    repliques: [
      `En haut à droite : « J1 · 3 h/${H_JOUR} ». Une journée simulée fait ${H_JOUR} heures.`,
      "Chaque action en croque un morceau. Un short en prend plusieurs : tu ne feras pas tout, tous les jours.",
      `Dormir coûte ${H_NUIT} h et fait avancer l'horloge : c'est la nuit qui fait tourner le monde.`,
      "Tes vidéos continuent de travailler pendant ce temps-là : les vues tombent, les abonnés arrivent.",
    ],
    // ⟦C57 · lot 4⟧ Quatre répliques, une idée chacune (1re : l'horloge de l'en-tête, 3e : le bouton Dormir).
    repliques_courtes: [
      `Une journée simulée fait ${H_JOUR} h : regarde en haut à droite.`,
      "Chaque action croque des heures : tu ne feras pas tout.",
      `Dormir coûte ${H_NUIT} h et fait avancer l'horloge.`,
      "Pendant la nuit, tes vidéos travaillent : vues et abonnés tombent.",
    ],
  },
  // ⟦C23 §2⟧ Retour de Matt : « ça n'a rien à faire dans le menu du bas [la fenêtre Actions],
  // passe-les dans les paramètres. » Le bouton « i » (`expliquerLaNuit`, dialogue maison) est
  // retiré de `Studio.tsx` : son texte devient CETTE info-bulle non bloquante, ancrée sur
  // « dormir ». Contrairement aux autres info-bulles de cette table, jamais due par un `quand()`
  // automatique : `Studio.tsx` la demande à la main (`decouverteStore.demanderBulle("dormir")`)
  // au premier survol/focus du bouton Dormir, la mémoire "vue" faisant le reste (une seule fois
  // par run, comme toutes les autres leçons).
  //
  // ⟦C29 · lot C · point 6⟧ Retour de Matt du 14/09 : « j'ai eu deux fois le message […]
  // pendant que tu dors, la vie continue. » La mémoire "vues" (`decouverteStore`, testée plus
  // bas) empêchait déjà qu'UNE MÊME leçon ne se rejoue dans une partie, la cause réelle était
  // ailleurs : cette bulle et la leçon `horloge` (bloquante, plus bas dans cette table)
  // disaient, en substance, la MÊME idée (« la nuit fait tourner le monde, tes vidéos
  // continuent ») sous deux ids différents, donc deux textes différents, et la mémoire ne
  // pouvait rien contre ça, chacun n'étant dit qu'une fois. Un joueur qui survole Dormir avant
  // sa première publication (très probable : c'est l'un des deux seuls boutons de la chambre au
  // tour 0) entend cette bulle, puis entend `horloge` juste après sa première publication : deux
  // messages, une idée. Corrigé à la racine : cette bulle ne redit plus ce que `horloge` couvre
  // déjà (l'avancée du monde), elle porte désormais l'idée qui n'est écrite NULLE PART ailleurs
  // dans la table, le risque d'enchaîner les nuits pour faire passer le temps.
  {
    id: "dormir",
    icone: "😴",
    titre: "Une nuit, pas dix",
    chapeau: "Dormir n'est pas une pause",
    corps: [
      "Dormir n'est pas une pause bouton : c'est ce qui fait passer à la nuit suivante, avec tout ce que ça déplace ailleurs dans le jeu.",
      "Une vraie nuit te remet d'aplomb. Mais enchaîner les nuits pour laisser passer le temps, c'est payer des journées entières sans rien publier.",
    ],
    aRetenir: "Une nuit remet d'aplomb. Les enchaîner pour laisser filer le temps coûte des journées entières sans rien publier.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Enchaîner les nuits coûte des journées entières sans publier.",
    bloquante: false,
    ancre: "dormir",
    quand: () => false,
  },
  {
    id: "energie",
    icone: "⚡",
    titre: "L'énergie",
    chapeau: "Première jauge",
    corps: [
      "Tout ce que tu fais en coûte, et elle ne remonte qu'en dormant. C'est elle qui décide du nombre de choses que tu peux faire dans une journée.",
      `Sous ${SEUIL_CRUNCH} ⚡ tu passes en crunch : tu peux continuer à travailler, mais ce que tu produis perd ${PCT_CRUNCH} % de sa force.`,
    ],
    aRetenir: `Sous ${SEUIL_CRUNCH} ⚡, ce que tu produis vaut ${PCT_CRUNCH} % de moins. Va dormir.`,
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: `Sous ${SEUIL_CRUNCH} ⚡, ce que tu produis vaut ${PCT_CRUNCH} % de moins : dors.`,
    bloquante: false,
    ancre: "energie",
    quand: (s) => s.energy < 100,
  },
  {
    id: "veille",
    etage: "veille",
    icone: "🔭",
    titre: "La veille",
    chapeau: "D'où viennent les idées",
    corps: [
      // ⟦C62 · lot événements · A-179⟧ Un seul mot, « idée » : ni « post-it », ni « cartes en main ».
      "Ces idées, ce sont les sujets de l'écran « Nouvelle vidéo ». Chaque vidéo en consomme au moins une, et sans idée en main il n'y a rien à tourner.",
      "La fenêtre est donc le robinet : quelques heures de ta journée contre des idées en main. C'est la respiration entre deux productions, pas du temps perdu.",
    ],
    aRetenir: "Pas d'idée en main, pas de vidéo. La fenêtre les ramène.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Pas d'idée en main, pas de vidéo : la fenêtre les ramène.",
    bloquante: false,
    ancre: "veille",
    quand: (s) => ouvert(s, "veille"),
  },
  {
    id: "commentaires",
    etage: "commentaires",
    icone: "💬",
    titre: "Les commentaires — et ce qu'ils te font",
    titre_court: "Les commentaires",
    chapeau: "La santé mentale",
    corps: [
      "Les lire remonte la fidélité de ton public : ceux qui se sentent lus reviennent. Et ça t'expose, aussi.",
      `D'où la jauge ❤ juste à côté de l'énergie : la santé mentale. Elle descend quand tu encaisses, quand tu enchaînes, quand tu ne dors pas. Sous 60 tu es fatigué — ${PCT_FATIGUE} % sur tout ce que tu produis. Sous 30, tu es en zone rouge.`,
      // ⟦A-29⟧ Cette ligne promettait « la thérapie » à un joueur qui n'a pas d'argent et n'en
      // aura pas avant le Grand Saut : elle l'envoyait chercher une porte qui n'existe pas
      // encore. Le remède de l'arc 1 est le sommeil, et c'est un CHOIX de design (voir
      // `SM_NUIT`, `shared/src/engine/actions.ts`) — donc on le dit franchement.
      `Et à zéro, c'est une fin de partie. Ton remède, ici, c'est de DORMIR : une vraie nuit rend ${SM_NUIT} de santé mentale, et c'est gratuit. Il y en aura d'autres plus tard : passé les ${PALIER_GRAND_SAUT} abonnés, et le jour où tu découvriras qu'on peut annoncer une pause à son public.`,
    ],
    aRetenir: "La santé mentale est un game over. Dors avant d'en avoir besoin — c'est le seul remède que tu aies pour l'instant.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "La santé mentale à zéro, c'est game over : dors avant.",
    // ⟦A-129 · C11⟧ Reste BLOQUANTE, seule exception parmi les leçons d'état : c'est elle qui
    // annonce que la santé mentale à zéro termine la partie — une info-bulle qu'on ignore d'un
    // clic n'a pas le droit de porter un avertissement de fin de partie.
    bloquante: true,
    quand: (s) => ouvert(s, "commentaires"),
    // ⟦C21 · A-146 §2.1⟧ Découpé, pas réécrit. Halo (`game/parole.ts`, `HALO_COMMENTAIRES`) : la
    // jauge santé mentale sur la deuxième réplique, le bouton Dormir sur la troisième.
    repliques: [
      "Les lire remonte la fidélité de ton public. Et ça t'expose, aussi.",
      `D'où la jauge ❤ juste à côté de l'énergie : la santé mentale. Sous 60 tu es fatigué, sous 30 tu es en zone rouge.`,
      `Et à zéro, c'est une fin de partie. Ton remède, ici : dormir, une vraie nuit rend ${SM_NUIT} de santé mentale, et c'est gratuit.`,
    ],
    // ⟦C57 · lot 4⟧ Trois répliques, une idée chacune (2e : la jauge ❤, 3e : le bouton Dormir).
    repliques_courtes: [
      "Les lire remonte ta fidélité, mais ça t'expose.",
      "Ta santé mentale ❤ : fatigué sous 60, zone rouge sous 30.",
      `À zéro, c'est la fin : dors, une vraie nuit rend ${SM_NUIT} ❤.`,
    ],
  },
  // ⟦SPEC-FIL §9.3 · A-9⟧ **Le Fil se découvre à l'arrivée de son PREMIER commentaire**, pas à
  // un étage d'escalier — ⟦A-5⟧ n'en gagne aucun pour lui, et c'est délibéré : sous 50 abonnés
  // une chaîne n'a pas de section commentaires, donc l'ouverture par palier aurait promis un
  // écran vide. Le déclencheur est le drapeau que le moteur pose lui-même (`arriveeFil`) : rien
  // n'est recopié ici, et la leçon ne peut pas se déclencher avant que le Fil existe vraiment.
  //
  // Elle vient APRÈS la leçon de santé mentale, qui prépare le terrain : celle-là dit ce que
  // lire coûte, celle-ci dit ce qu'on peut en faire.
  {
    id: "fil",
    icone: "💬",
    titre: "Le Fil",
    chapeau: "Quelqu'un t'a écrit",
    corps: [
      "Ta section commentaires n'est plus vide. Le bouton « Lire les commentaires » ouvre le Fil : les messages s'y empilent à chaque publication et à chaque nuit, et ils en repartent tout seuls au bout d'une semaine si tu ne les lis pas.",
      "Tu réponds trois par session, et tu choisis comment : un cœur, une vanne, une épingle, ou rien du tout. Supprimer existe aussi — ça ne coûte rien sur le moment, et ça finit par se remarquer.",
      "Tout ça alimente une nouvelle jauge, la Hype : l'énergie de ta communauté. Elle pousse tes vues, et elle retombe toute seule quand tu disparais. Au-dessus de 70, ton public partage ta prochaine vidéo.",
    ],
    aRetenir: "Répondre construit la Hype. Ne rien lire ne coûte rien — jusqu'à ce que le Fil déborde.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Répondre construit la Hype, ne rien lire finit par coûter.",
    bloquante: false,
    ancre: "fil",
    quand: (s) => s.flags.fil_decouvert === true,
  },
  {
    id: "image",
    etage: "image",
    icone: "🎭",
    titre: "L'image de chaîne",
    chapeau: "Ta ligne éditoriale",
    corps: [
      `Chaque sujet penche d'un côté : ☀️ lumière (bienveillant, propre) ou 🌀 chaos (provoc, putaclic). Le poster garde la moyenne de tes dernières vidéos. Au-dessus de +${SEUIL_LUMIERE} tu es une chaîne lumière, en dessous de ${SEUIL_CHAOS} une chaîne chaos.`,
      "Aucune des deux n'est meilleure : le chaos fait plus de vues, la lumière fidélise davantage. Ce qui coûte vraiment, c'est de zigzaguer entre les deux.",
    ],
    aRetenir: "Choisis une ligne. Le public pardonne beaucoup, sauf l'incohérence.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Choisis une ligne : le public pardonne beaucoup, sauf l'incohérence.",
    // ⟦A-129 · C11⟧ Pas d'ancre DOM dédiée : l'indicateur vit dans l'écran « Nouvelle vidéo »,
    // pas toujours affiché quand la condition devient vraie. Repli en position par défaut —
    // explicitement permis, jamais un bug.
    bloquante: false,
    quand: (s) => ouvert(s, "image"),
  },
  {
    id: "tendances",
    etage: "tendances",
    icone: "🔥",
    titre: "Les tendances",
    chapeau: "Surfer, ou pas",
    corps: [
      "Chacune a une vie : émergente, puis pic, puis saturée, puis morte. Le tableau te dit où elle en est.",
      "Un sujet qui touche une tendance chaude sort en classe 🔥 : beaucoup de vues, très vite, et plus rien ensuite. Un sujet froid vit moins fort mais beaucoup plus longtemps.",
      "Arriver trop tard sur une tendance saturée, c'est faire la même vidéo que trois cents autres personnes. D'où le « ou pas ».",
    ],
    aRetenir: "La tendance donne le pic. Le sujet intemporel donne la rente.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "La tendance donne le pic, l'intemporel donne la rente.",
    bloquante: false,
    quand: (s) => ouvert(s, "tendances"),
  },
  {
    id: "arbre",
    etage: "arbre",
    icone: "🌳",
    titre: "L'arbre de compétences",
    chapeau: "Tes points de compétence",
    corps: [
      `Les points tombent quand tu montes de niveau (${points(PC_PAR_NIVEAU)}) et à chaque palier d'abonnés (${points(PC_PAR_PALIER)}). Il n'y en aura jamais assez pour tout prendre.`,
      "Chaque nœud change quelque chose de concret et de visible : une fenêtre plus large dans un mini-jeu, une aide qui pré-remplit une étape, du matériel de tournage.",
    ],
    aRetenir: "Des points qui dorment ne servent à rien. Place-les, même mal.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Des points qui dorment ne servent à rien : place-les.",
    bloquante: false,
    ancre: "arbre",
    quand: (s) => ouvert(s, "arbre"),
  },

  // ── Les trois jauges qui n'ont pas de moment dans l'arc 1 : elles se présentent quand elles
  //    bougent pour la première fois. Elles sont APRÈS l'arc dans la table, donc elles ne
  //    peuvent jamais doubler une leçon de l'arc. ⟦A-9 : « y compris les 7 jauges »⟧
  {
    id: "reputation",
    icone: "⭐",
    titre: "La réputation",
    chapeau: "Ce que le milieu pense de toi",
    corps: [
      "Elle vient de bouger. La réputation, c'est ce que la plateforme et les autres créateurs pensent de toi : elle ouvre des portes — collabs, sponsors, opportunités — et elle amortit les tuiles.",
      "Elle tombe vite sur un bad buzz, et remonte lentement.",
    ],
    aRetenir: "La réputation se perd en une vidéo et se répare en dix.",
    bloquante: false,
    ancre: "reputation",
    quand: (s) => s.reputation !== 50,
  },
  {
    id: "fidelite",
    icone: "🤝",
    titre: "La fidélité",
    chapeau: "Ceux qui répondent présent",
    corps: [
      "La part de ton public qui te suit vraiment. C'est elle qui décide de combien de gens voient ta prochaine vidéo dès la première heure — et de combien restent le jour où tu te plantes.",
      "Elle monte quand tu lis tes commentaires. Elle descend quand tu disparais.",
    ],
    aRetenir: "Les abonnés, c'est un nombre. La fidélité, c'est qui vient vraiment.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Les abonnés, un nombre ; la fidélité, ceux qui viennent vraiment.",
    bloquante: false,
    ancre: "fidelite",
    quand: (s) => s.loyalty !== 50,
  },
  {
    id: "momentum",
    icone: "🚀",
    titre: "Le momentum",
    chapeau: "La régularité, en chiffre",
    corps: [
      "Ce n'est pas une jauge sur 100 mais un multiplicateur : il pousse ou il freine tout ce que tu publies.",
      `Publier régulièrement le fait monter. Le silence le fait redescendre, d'environ ${PCT_MOMENTUM_JOUR} % par jour sans rien mettre en ligne.`,
    ],
    aRetenir: "La régularité est une statistique. Littéralement.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "La régularité est une statistique, littéralement.",
    bloquante: false,
    ancre: "momentum",
    quand: (s) => Math.abs(s.momentum - 1) > 0.001,
  },

  // ───────────────────────── Arc 2 et arc 3 : les ouvertures ─────────────────────────
  {
    id: "longue",
    etage: "longue",
    icone: "🎬",
    titre: "La vidéo longue",
    chapeau: "Cinq étapes, pas trois",
    corps: [
      "Une longue, c'est cinq étapes au lieu de trois — la miniature et la promotion s'ajoutent. Elle coûte bien plus cher en heures et en énergie, et elle rapporte à une autre échelle.",
      "À partir de maintenant, regarde la FORME de tes post-it avant leur titre : debout en 9:16, c'est un short. Couché en 16:9, c'est une longue.",
      "Une longue ne se juge pas le soir même : ses vues tombent nuit après nuit, pas d'un coup.",
    ],
    aRetenir: "Un short attrape. Une longue installe.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Un short attrape, une longue installe.",
    bloquante: false,
    ancre: "longue",
    quand: (s) => ouvert(s, "longue"),
  },
  // ⟦A-16⟧ « La première production longue mérite sa leçon. » Elle tombe APRÈS la première
  // longue publiée, juste avant que le récapitulatif des jours écoulés ne s'ouvre — A-9 veut
  // qu'on explique *avant que ça démarre*, et A-15 pose « prévenir, jamais surprendre ».
  {
    id: "temps",
    icone: "🕰",
    titre: "Le temps que ça coûte",
    chapeau: "Ce que trois jours font dans ton dos",
    corps: [
      "Tu viens de cliquer une fois, et le monde a avancé de plusieurs jours. Pendant ce temps il ne s'est pas arrêté : tes vidéos en ligne ont versé leurs vues jour après jour, les tendances ont bougé, et des idées que tu gardais en main ont fait leur temps.",
      `Et tu as dormi — mal. Une nuit passée à tourner ne rend que ${SM_NUIT_CONTENUE} de santé mentale, contre ${SM_NUIT} pour une vraie nuit dans ton lit.`,
    ],
    aRetenir: "Le temps n'est pas gratuit. Le récapitulatif te dit exactement ce qu'il a pris.",
    // ⟦C57 · A-174⟧ Version courte, écrite à côté du texte long (jamais à sa place).
    a_retenir_court: "Le temps coûte : le récap dit ce qu'il a pris.",
    bloquante: false,
    quand: (s) => s.videos.some((v) => v.format === "longue"),
  },
  {
    id: "argent",
    etage: "argent",
    icone: "₵",
    titre: "Les Clicks",
    chapeau: "Le Grand Saut",
    bloquante: false,
    ancre: "argent",
    // ⟦C60 · lot b · A-177⟧ Plus de charges, de boutique ni de logement : les Clicks ne servent qu'à payer. La leçon le dit,
    // et ne promet rien d'autre. ⟦C60 bis · A-177⟧ Matt (03/10/2026) : « les Clicks ne paient plus que le Marché et l'Équipe » :
    // les nœuds de l'Arbre et le respec se paient en points, la séance de Souffler en temps et en énergie.
    corps: [
      `Jusqu'ici l'argent n'existait pas dans ce jeu : il fallait ${PALIER_GRAND_SAUT} abonnés pour qu'il compte. Désormais tes vues rapportent des Clicks.`,
      "Ils servent à payer : des freelances au Marché, et ton Équipe. Rien d'autre ne te les prend, et rien ne tombe tout seul.",
      // ⟦A-11 §1⟧ Le trou de l'arc 3, mesuré : l'économie est calibrée pour un joueur qui
      // publie des LONGUES, et celui qui arrive ici publie encore des shorts. Rien ne le
      // disait — c'est dit ici, avec les RPM du moteur et pas des chiffres recopiés.
      `Et ce sont les LONGUES qui paient : ${String(RPM_LONGUE).replace(".", ",")} ₵ pour 1 000 vues, contre ${String(RPM_SHORT).replace(".", ",")} ₵ pour un short. Les shorts font venir du monde, ils ne remplissent pas la caisse.`,
      // ⟦A-29⟧ « Ce qui coûte de l'argent est dans le marché » : la contrepartie de la leçon
      // des commentaires, où l'on promet au joueur que d'autres remèdes viendront. Les voici.
      "Et un remède de plus, sur l'écran Souffler : voir quelqu'un. Une séance remonte la santé mentale sans te coûter une semaine de silence. Pas de Clicks à sortir : elle prend du temps et de l'énergie.",
    ],
    aRetenir: "Les Clicks ne servent qu'à payer : le Marché et l'Équipe.",
    quand: (s) => ouvert(s, "argent"),
  },
];

/**
 * Les leçons de mini-jeu — **avant chaque mini-jeu joué pour la première fois** ⟦A-9⟧.
 *
 * ⟦A-129 · C11⟧ **Depuis C11, elles ne passent plus par une modale séparée.** Avant : l'Atelier
 * les demandait au guetteur (`decouverteStore`) et ne montait pas le mini-jeu tant qu'elles
 * n'étaient pas lues, un écran de plus, sur un chrono qui n'avait même pas commencé. Leur
 * contenu (`corps`/`aRetenir`) est fusionné **dans l'écran d'intro du mini-jeu lui-même**
 * (`Atelier.tsx`), la première fois seulement. `decouverteStore.marquerVue(id)` les marque lues
 * au clic sur Jouer, plus de `demander`/`courante`, ces leçons n'occupent jamais le voile
 * bloquant.
 *
 * ⟦C23 §4⟧ **Retour de Matt : « 3 pop-ups d'explications pour chaque nouveau mini-jeu, beaucoup
 * trop long », un short en enchaînant trois d'affilée la première fois.** L'écran d'intro ne
 * porte plus les 2-3 paragraphes de `corps` : il montre Matt dans sa boîte (`BoiteDialogue`,
 * `Atelier.tsx`) avec les deux `repliques` ci-dessous (la règle du geste, puis `aRetenir`),
 * « Jouer » sous la boîte. Les fois suivantes, plus d'écran d'intro DU TOUT : `Atelier.tsx`
 * saute directement en phase `jeu` ; la règle en une phrase (`REGLES`, en bas de ce fichier)
 * reste lisible en permanence dans `PiedAtelier`, rappelable en réplique par un bouton « ? ».
 * `corps`/`aRetenir` restent la source si un écran hors Atelier en avait un jour besoin,
 * `repliquesDe()` y retombe déjà pour toute leçon sans `repliques` écrites.
 *
 * Les textes d'Écriture répondent à un trou du playtest : ⟦C52⟧ les colonnes du script (et la pub
 * qu'on laisse filer).
 * ⟦C53 · A-170 §2⟧ Le Tournage est devenu « La prise parfaite » : sa leçon dit la jauge, REC dans
 * le vert, et l'acteur qui éternue (le seul « ne pas faire » du jeu).
 */
/**
 * ⟦C57 · lot 8 · A-174⟧ **`court`** : la réplique de Matt en UNE phrase de 12 mots au plus, écrite À CÔTÉ
 * des deux `repliques` d'origine (jamais à leur place). C'est elle que l'intro d'un mini-jeu affiche ;
 * les deux répliques, le `corps` et `aRetenir` restent entiers et se relisent derrière « Les dessous »
 * (`atelier/core/DessousMiniJeu.tsx`). Le champ vit ici, sur le type des leçons de mini-jeu, et pas sur
 * l'interface partagée `Lecon` : les autres leçons (la chambre) ne le portent pas.
 */
export type LeconMiniJeu = Lecon & { court: string };

export const LECONS_MINIJEU: Record<AtelierStage, LeconMiniJeu> = {
  ecriture: {
    id: "minijeu:ecriture",
    icone: "✍️",
    titre: "Le script qui tombe",
    chapeau: "Écriture · ranger",
    corps: [
      "Un script, ce sont des parties : l'accroche qui ouvre, l'histoire qui s'installe, la chute qui fait rire, l'appel qui te fait suivre. Chaque phrase qui tombe appartient à l'une d'elles, et à elle seule.",
      "Tu pousses le bloc dans la bonne colonne avant qu'il touche le sol : ← → pour le déplacer, ↓ pour le lâcher, ou une colonne au toucher. Ça va de plus en plus vite.",
      "Une pub douteuse, avec ses rayures, ne se range nulle part : tu la laisses filer. Un bloc que tu ne touches pas reste là où il est tombé.",
    ],
    aRetenir: "Chaque phrase a sa colonne. Une pub rayée, tu la laisses filer.",
    // ⟦C23 §4⟧ Deux répliques maximum : la règle du geste, puis `aRetenir` (jamais retapé).
    repliques: [
      "Les phrases de ton script tombent : pousse chacune dans la bonne colonne avant qu'elle touche le sol, avec ← → et ↓, ou au toucher.",
      "Chaque phrase a sa colonne. Une pub rayée, tu la laisses filer.",
    ],
    // ⟦C57 · lot 8⟧ La forme courte, à côté des deux répliques : l'intro n'affiche qu'elle.
    court: "Chaque phrase dans sa colonne : une pub rayée, tu la laisses filer.",
  },
  tournage: {
    id: "minijeu:tournage",
    icone: "🎥",
    titre: "La prise parfaite",
    chapeau: "Tournage · jouer",
    corps: [
      "Tu es devant la caméra. Un curseur va et vient sur la jauge, et une zone verte marque le bon moment. Appuie sur REC, avec Espace, un clic ou un toucher, quand le curseur est dedans : plus tu es près du centre, mieux c'est.",
      "Cinq prises. À chaque prise la zone verte rétrécit et le curseur accélère. Une zone dorée peut briller au centre : la toucher rapporte un bonus qui rattrape un raté.",
      "Il arrive que ça se corse : la zone glisse, le curseur change de vitesse, ou l'acteur éternue. Quand il renifle, ne coupe pas : laisse passer le curseur et appuie au passage suivant.",
    ],
    aRetenir: "Dans le vert, au plus près du centre. Et quand l'acteur éternue, on ne coupe pas.",
    // ⟦C23 §4⟧ Deux répliques maximum : la règle du geste, puis `aRetenir`.
    repliques: [
      "Appuie sur REC (Espace, clic ou toucher) quand le curseur est dans le vert, cinq prises de suite : la zone rétrécit et le curseur accélère.",
      "Dans le vert, au plus près du centre. Et quand l'acteur éternue, on ne coupe pas.",
    ],
    // ⟦C57 · lot 8⟧ La forme courte, à côté des deux répliques : l'intro n'affiche qu'elle.
    court: "REC quand le curseur est dans le vert : si l'acteur éternue, attends.",
  },
  montage: {
    id: "minijeu:montage",
    icone: "✂️",
    titre: "Couper dans le rythme",
    chapeau: "Montage · rythmer",
    corps: [
      "Les marqueurs défilent vers la ligne de coupe. Frappe quand un marqueur la traverse — clic, Espace ou Entrée. Plus tu es près du beat, plus le cut vaut cher.",
      "Ce n'est pas un jeu de vitesse, c'est un jeu de régularité : trouve le tempo, et laisse ta main dessus. Un cut en avance vaut moins qu'un cut à l'heure.",
    ],
    aRetenir: "Sur le temps, pas avant. Le rythme se tient, il ne se course pas.",
    // ⟦C23 §4⟧ Deux répliques maximum : la règle du geste, puis `aRetenir`.
    repliques: [
      "Frappe (clic, Espace ou Entrée) juste quand un marqueur traverse la ligne de coupe.",
      "Sur le temps, pas avant. Le rythme se tient, il ne se course pas.",
    ],
    // ⟦C57 · lot 8⟧ La forme courte, à côté des deux répliques : l'intro n'affiche qu'elle.
    court: "Frappe quand le marqueur touche la ligne : sur le temps, pas avant.",
  },
  miniature: {
    id: "minijeu:miniature",
    icone: "🖼️",
    titre: "Le putaclic-o-mètre",
    chapeau: "Miniature + titre · doser",
    corps: [
      "Deux ou trois curseurs, une seule aiguille pour en rendre compte. Tu vises la zone verte — mais l'aiguille a de l'inertie : elle dépasse, elle revient, elle tremble.",
      "À la fin du chrono, elle fige exactement où elle est. Donc : dose tôt, puis arrête d'y toucher et laisse-la s'amortir. Pousser le putaclic à fond t'emmène dans le rouge, et le rouge, on ne voit pas où il commence.",
    ],
    aRetenir: "Règle tôt, lâche les curseurs, laisse l'aiguille se poser.",
    // ⟦C23 §4⟧ Deux répliques maximum : la règle du geste, puis `aRetenir`.
    repliques: [
      "Règle les curseurs pour viser la zone verte, puis lâche-les : l'aiguille a de l'inertie et fige à la fin du chrono.",
      "Règle tôt, lâche les curseurs, laisse l'aiguille se poser.",
    ],
    // ⟦C57 · lot 8⟧ La forme courte, à côté des deux répliques : l'intro n'affiche qu'elle.
    court: "Règle tôt, lâche les curseurs, laisse l'aiguille se poser.",
  },
  promotion: {
    id: "minijeu:promotion",
    icone: "📣",
    titre: "La Découpe",
    chapeau: "Promotion · parier",
    corps: [
      "La barre, c'est ta vidéo entière. Tu poses des extraits là où tu crois que les meilleurs moments se trouvent, et tu envoies chacun sur la plateforme qui lui va.",
      "Tu ne vois pas où sont vraiment les temps forts avant de valider : c'est un pari, pas une mesure. Ce que tu as réussi aux étapes précédentes te dit quand même où chercher.",
    ],
    aRetenir: "Un extrait bien placé sur la bonne plateforme vaut trois extraits au hasard.",
    // ⟦C23 §4⟧ Deux répliques maximum : la règle du geste, puis `aRetenir`.
    repliques: [
      "Pose des extraits sur la barre de ta vidéo, là où tu crois les meilleurs moments, et envoie chacun sur la bonne plateforme.",
      "Un extrait bien placé sur la bonne plateforme vaut trois extraits au hasard.",
    ],
    // ⟦C57 · lot 8⟧ La forme courte, à côté des deux répliques : l'intro n'affiche qu'elle.
    court: "Pose tes extraits sur les bons moments, sur la bonne plateforme.",
  },
};

/**
 * ⟦C60 · lot b · A-177⟧ **Les leçons que le grand ménage retire** : celles que `meta.lecons_par_systeme` range
 * sous un système COUPÉ (la leçon `image`, de l'alignement). Elles restent écrites dans `LECONS`, mais
 * `prochaineLecon` et `prochaineInfoBulle` ne les montrent plus. `leconDeLEtage` les connaît toujours : une
 * leçon retirée continue de « prendre en charge » l'annonce de son étage, donc `MomentsDebloques` se tait
 * aussi, et rien d'un système coupé n'est annoncé par la bande. Aucun interrupteur ⇒ aucune leçon retirée
 * ⇒ le jeu d'avant, au caractère près.
 */
function leconsRetirees(): ReadonlySet<LeconId> {
  return retiresParLeMenage(contenu(), "lecons_par_systeme");
}

const PAR_ID = new Map<LeconId, Lecon>([
  ...LECONS.map((l) => [l.id, l] as const),
  ...Object.values(LECONS_MINIJEU).map((l) => [l.id, l] as const),
]);

export function lecon(id: LeconId): Lecon | null {
  return PAR_ID.get(id) ?? null;
}

/**
 * Le texte complet de la leçon. Pour une leçon d'ouverture, l'**annonce de l'escalier** ⟦A-5⟧
 * ouvre le bal : c'est le « moment de jeu » que `MomentsDebloques` aurait affiché, et la leçon
 * l'a mangé pour ne pas empiler deux pop-ups. Une seule source, celle de l'escalier.
 */
export function paragraphes(l: Lecon): readonly string[] {
  return l.etage ? [etage(l.etage).annonce, ...l.corps] : l.corps;
}

/** La leçon qui prend en charge l'annonce de cet étage ⟦A-5⟧, s'il y en a une. */
export function leconDeLEtage(etage: EtageId): Lecon | null {
  return LECONS.find((l) => l.etage === etage) ?? null;
}

/**
 * La prochaine leçon **bloquante** à montrer, ou `null` — ⟦A-129 · C11⟧ au plus 4 dans toute
 * la table (`briefing`, `evenement`, `horloge`, `commentaires`). La règle « une leçon par
 * tour » est ici : seul le briefing d'ouverture (tour 0) s'autorise à enchaîner.
 */
export function prochaineLecon(
  state: GameState,
  vues: ReadonlySet<LeconId>,
  tourDeLaDerniere: number | null,
): Lecon | null {
  // Une run finie n'apprend plus rien : expliquer la réputation par-dessus l'écran de burnout
  // serait la pire pop-up du jeu.
  if (state.status !== "active") return null;
  if (state.turn !== 0 && state.turn === tourDeLaDerniere) return null;
  const retirees = leconsRetirees();
  return (
    LECONS.find(
      (l) => l.bloquante === true && !vues.has(l.id) && !retirees.has(l.id) && (l.quand?.(state) ?? false),
    ) ?? null
  );
}

/**
 * ⟦A-129 · C11⟧ La prochaine **info-bulle non bloquante** à montrer, ou `null`. Pas de règle
 * « une par tour » ici : le jeu ne s'arrête plus, donc rien n'empêche une deuxième info-bulle de
 * suivre la première dès qu'elle se ferme, y compris dans le même tour — seule contrainte,
 * portée par l'appelant : une seule à l'écran à la fois.
 */
export function prochaineInfoBulle(state: GameState, vues: ReadonlySet<LeconId>): Lecon | null {
  if (state.status !== "active") return null;
  const retirees = leconsRetirees();
  return (
    LECONS.find(
      (l) => l.bloquante === false && !vues.has(l.id) && !retirees.has(l.id) && (l.quand?.(state) ?? false),
    ) ?? null
  );
}

/**
 * ⟦C16⟧ **Une modale de jeu qui doit faire attendre une info-bulle.**
 *
 * Verbatim de Mathieu : *« la pop-up qui expliquait la communauté pendant qu'il y avait la
 * pop-up qui faisait le premier événement […] ça se marche dessus et du coup tu perds les
 * infos »*. La leçon coupable est `fil` (`bloquante: false`, ⟦A-129 · C11⟧) : son `quand`
 * (`flags.fil_decouvert`) ne regardait jamais si un `pending_event`/une offre dépliée occupait
 * déjà l'écran — `InfoBulleDecouverte` n'avait tout simplement pas cette information.
 *
 * Volontairement absent d'ici : la leçon `evenement` (`bloquante: true`), dont le `quand` EST
 * `pending_event !== null` — elle doit au contraire s'afficher PENDANT le `pending_event`, avant
 * que `EvenementModal` ne se montre (`⟦A-9⟧`, ce fichier). `EvenementModal` s'efface déjà tant
 * qu'une leçon bloquante est affichée (`useDecouverteStore().courante`, son propre `suspendus`)
 * — la réciproque, côté BLOQUANT, n'a donc pas besoin d'un garde de plus ici. Ce garde-ci ne
 * sert que les info-bulles non bloquantes, qui elles n'ont aucune raison de savoir ce qu'est un
 * `pending_event`.
 *
 * Un `pending_event`/une offre ne « consomme » rien de la leçon en attente : aucun flag
 * `quand()` n'est remis à zéro par la fermeture d'un événement, donc la leçon reste due et
 * s'affiche dès que `libre` redevient vrai — « aucune leçon perdue silencieusement ».
 */
export function modaleEvenementOuverte(
  pendingEvent: unknown,
  offreOuverte: string | null,
): boolean {
  return pendingEvent !== null && pendingEvent !== undefined ? true : offreOuverte !== null;
}
